import { KST } from '../model/time';

/**
 * 2days Ops 콘솔 (운영진) — 프로젝트 하나를 만들고 시작하기까지의 Setup 6단계.
 * 단계마다 Save 가 API 한 번이고(/two-days/admin/projects), 응답으로 받은 프로젝트 전체로 화면을 바꾼다.
 *
 * project = {
 *   id, name, domain, status: 'draft' | 'active' | 'ended',
 *   leaderStartAt, endAt,              // ISO. endAt 기본값 = 리더 시작 + 49h (운영진만 바꿀 수 있다)
 *   brief: { scenario, deliverable, jobs[], handoffs[] },
 *   teams: [{ id, name, slug, members: [{ id, name, email, job, status, staffBackup }] }],
 *   tasks: { [teamId]: [{ id, title, assigneeId, receiverId, dueAt, taskList[] }] },
 *   checkpoints: [{ id, name, offsetH, scheduledAt }],   // 브리프를 저장하면 만들어진다
 *   done: { project, brief, teams, tasks, checkpoints, startEnd },
 * }
 */

const HOUR = 3_600_000;

/** 리더는 팀원보다 1시간 먼저 시작한다 (MVP §8-8) */
export const TEAM_START_H = 1;
/** 마지막 태스크 마감 = 리더 시작 + 47h — 끝나기 2시간 전 */
export const LAST_DUE_H = 47;
/** 프로젝트 종료 = 리더 시작 + 49h (2h 여유 포함) */
export const END_H = 49;

export const addHours = (iso, h) =>
  iso ? new Date(new Date(iso).getTime() + h * HOUR).toISOString() : null;

export const periodOf = (leaderStartAt, endOverride) => ({
  teamStartAt: addHours(leaderStartAt, TEAM_START_H),
  lastDueAt: addHours(leaderStartAt, LAST_DUE_H),
  endAt: endOverride || addHours(leaderStartAt, END_H),
});

/** "Oct 15, 2026 · 10:00" (KST) */
export const formatKst = (iso, { year = true } = {}) => {
  if (!iso) return '';
  const d = new Date(iso);
  const date = new Intl.DateTimeFormat('en-US', {
    timeZone: KST,
    month: 'short',
    day: 'numeric',
    ...(year ? { year: 'numeric' } : {}),
  }).format(d);
  const time = new Intl.DateTimeFormat('en-GB', {
    timeZone: KST,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
  return `${date} · ${time}`;
};

/** 프로젝트 선택기에 쓰는 기간 — "Oct 15–17" */
export const rangeLabel = (startIso, endIso) => {
  if (!startIso) return '';
  const fmt = (iso, opts) =>
    new Intl.DateTimeFormat('en-US', { timeZone: KST, ...opts }).format(new Date(iso));
  const sm = fmt(startIso, { month: 'short' });
  const em = fmt(endIso, { month: 'short' });
  const sd = fmt(startIso, { day: 'numeric' });
  const ed = fmt(endIso, { day: 'numeric' });
  return sm === em ? `${sm} ${sd}–${ed}` : `${sm} ${sd} – ${em} ${ed}`;
};

export const JOBS = [
  { key: 'planner', label: 'PM' },
  { key: 'designer', label: 'Designer' },
  { key: 'developer', label: 'Developer' },
  { key: 'marketer', label: 'Marketer', optional: true },
];
export const jobLabel = (key) => JOBS.find((j) => j.key === key)?.label ?? key;

/** 받는 직무 이름 — 개발 결과를 PM 이 받으면 "PM (QA)", 비어 있으면 팀장 */
export const receivingRoleLabel = (task) => {
  if (!task.defaultReceiverRole) return 'Team lead Kim';
  const label = jobLabel(task.defaultReceiverRole);
  return task.role === 'developer' && task.defaultReceiverRole === 'planner'
    ? `${label} (QA)`
    : label;
};

/** 인계 유형 — 받는 사람이 확인할 Task List 기본 항목. 최종 결과물 → 팀장은 항상 포함.
 *  링크(Figma · 테스트 링크 · 결과물 링크)는 Submit 의 인계 유형별 슬롯으로 받아 항목에서 뺐다 */
export const HANDOFFS = [
  {
    key: 'planning_design',
    title: 'PM → Design',
    items: 'Task List · Screen list · Priority by screen · Required data and copy · Open decisions',
  },
  {
    key: 'design_dev',
    title: 'Design → Development',
    items: 'Task List · States per screen · Spacing, color and component specs · Asset export',
  },
  {
    key: 'dev_qa',
    title: 'Development → PM (QA)',
    items:
      'Task List · Core scenario works end to end · Empty and error states handled · Connected to the sample data · Checked on a mobile screen · Built and excluded scope · Known bugs',
  },
  {
    key: 'any_marketing',
    title: 'Any → Marketing (optional)',
    items: 'Task List · Key message · Screenshots · Target',
  },
  {
    key: 'final_npc',
    title: 'Final deliverable → Team lead Kim',
    items: 'Task List · All brief completion criteria · Rubric items',
    always: true,
  },
];

/** 운영진 쪽 팀장 — 받는 사람을 정할 직군이 팀에 없을 때 받는다 */
export const NPC_LEAD = { id: 'npc', name: 'Team lead Kim', job: 'npc_lead' };

/**
 * 팀 리더 = PM (서버와 같은 규칙). 신청자 PM 이 있으면 그 사람, 없으면(또는 이탈하면) 그 팀의 스태프 PM.
 * PM 이 아무도 없으면 리더 없음 — 다른 직무를 대신 세우지 않는다. 이탈한 PM 은 리더가 아니다.
 */
const isActivePm = (m) => m.status !== 'dropped' && m.job === 'planner';
export const leaderOf = (team) =>
  team.members.find((m) => !m.staff && isActivePm(m)) ??
  team.members.find((m) => m.staff && isActivePm(m)) ??
  null;
export const leaderIdOf = (team) => leaderOf(team)?.id ?? null;

/** 받는 사람 자동 지정 (A†) — 기획 → 디자인 → 개발 → 기획(QA), 마케터 → 기획. 없으면 팀장 */
export const NEXT_JOB = {
  planner: 'designer',
  designer: 'developer',
  developer: 'planner',
  marketer: 'planner',
};
/** 이 팀에서 job 직무의 신청자 팀원 (스태프 제외) */
export const realMemberOf = (members, job) => members.find((m) => !m.staff && m.job === job);

/** 직무가 정해진 새 태스크의 받는 직무 — 인계 순서 (기획 → 디자인 → 개발 → 기획 QA) */
export const receiverRoleFor = (role) => NEXT_JOB[role] ?? null;

/**
 * 받는 사람 미리 보기 — 받을 직무의 신청자 팀원 → 이미 들어온 스태프 → (저장하면) 그 직무의 스태프 → 팀장.
 * 서버가 저장할 때 같은 규칙으로 정한다.
 */
export const receiverPreview = (members, receiverRole, staffList = []) => {
  if (!receiverRole) return NPC_LEAD;
  return personForRole(members, receiverRole, staffList) ?? NPC_LEAD;
};

/**
 * 이 직무를 맡을 사람 미리 보기 — 신청자 팀원 → 이 팀에 들어온 스태프 → 등록된 그 직무의 스태프(저장하면 들어온다).
 * 아무도 없으면 null (Unassigned).
 */
export const personForRole = (members, job, staffList = []) => {
  if (!job) return null;
  const registered = staffList.find((s) => s.job === job);
  return (
    realMemberOf(members, job) ??
    members.find((m) => m.staff && m.job === job) ??
    (registered ? { id: null, name: registered.name, job, staff: true } : null)
  );
};

export const receiverFor = (assignee, members) => {
  if (!assignee) return null;
  const next = members.find((m) => m.job === NEXT_JOB[assignee.job] && m.id !== assignee.id);
  return next ?? NPC_LEAD;
};

/** 새 태스크 번호 — 직무 첫 글자(P·D·V·M) + 팀 안에서 다음 번호 */
const CODE_PREFIX = { planner: 'P', designer: 'D', developer: 'V', marketer: 'M' };
export const nextTaskCode = (job, list) => {
  const prefix = CODE_PREFIX[job] ?? 'T';
  const used = new Set(list.map((t) => t.code));
  let n = 1;
  while (used.has(`${prefix}${n}`)) n += 1;
  return `${prefix}${n}`;
};

/** "1h 30m" */
export const formatMinutes = (min) => {
  if (!min) return '';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return [h ? `${h}h` : '', m ? `${m}m` : ''].filter(Boolean).join(' ');
};

export const STEPS = [
  { key: 'project', label: 'Project' },
  { key: 'brief', label: 'Brief' },
  { key: 'teams', label: 'Team & roles' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'checkpoints', label: 'Checkpoints' },
  { key: 'startEnd', label: 'Start / End' },
];

/**
 * 시작한 뒤에는 Setup 을 거의 못 바꾼다 — 진행 중에는 종료 시각과 아직 안 온 체크포인트만.
 * 끝난 뒤에는 전부 읽기 전용.
 */
export const setupMode = (project) =>
  !project || project.status === 'draft' ? 'draft' : project.status;

/** 단계 목록에 보이는 상태 — 진행 중·종료 후 */
export const LOCKED_STATUS = {
  active: {
    project: 'Editable · start locked',
    brief: 'Editable',
    teams: 'Admin edits',
    tasks: 'Admin edits',
    checkpoints: 'Upcoming editable',
    startEnd: 'Active',
  },
  ended: {
    project: 'Editable · start locked',
    brief: 'Editable',
    teams: 'Admin edits',
    tasks: 'Admin edits',
    checkpoints: 'Upcoming editable',
    startEnd: 'Ended',
  },
};

/** 마감을 늦출 수 있는 외부 사유 3종 — 늦춘 태스크는 기한 준수 집계에서 뺀다 (기한 준수 규칙 §5) */
export const EXTEND_REASONS = [
  { key: 'system_outage', label: 'System outage' },
  { key: 'member_dropout', label: 'Member dropout' },
  { key: 'admin_error', label: 'Admin error' },
];

/** 단계 카드 위 안내 */
export const lockedNotice = (mode, activeText = 'Read-only while active.') =>
  mode === 'active' ? activeText : mode === 'ended' ? 'Read-only after the project ends.' : null;

export const STATUS_CHIP = {
  draft: { label: 'Draft', tone: 'gray' },
  active: { label: 'Active', tone: 'success' },
  ended: { label: 'Completed', tone: 'accent' },
};

/** 종료 시각을 기본값(리더 시작 + 49h)에서 바꿨으면 "Extended" */
export const isExtended = (p) =>
  !!p.leaderStartAt &&
  !!p.endAt &&
  new Date(p.endAt).getTime() !== new Date(addHours(p.leaderStartAt, END_H)).getTime();

/**
 * 서버 응답(ProjectDetail)을 화면 모양으로 — 받는 사람이 비어 있으면 팀장이다.
 * 태스크 묶음의 키(팀 id)는 JSON 이라 문자열로 오지만 JS 객체 키라 그대로 쓴다.
 */
export const fromServer = (p) => ({
  ...p,
  tasks: Object.fromEntries(
    Object.entries(p.tasks ?? {}).map(([teamId, list]) => [
      teamId,
      list.map((t) => ({
        ...t,
        receiverId: t.receiverId ?? NPC_LEAD.id,
        dependsOn: t.dependsOn ?? [],
      })),
    ]),
  ),
  done: p.done ?? {},
});

/** 화면의 태스크 묶음을 서버 요청으로 — 팀장은 receiverId 없이 보낸다 */
export const tasksToServer = (tasks) =>
  Object.fromEntries(
    Object.entries(tasks).map(([teamId, list]) => [
      teamId,
      list.map((t) => ({
        code: t.code,
        title: t.title,
        role: t.role,
        assigneeId: t.assigneeId ?? null,
        // 받는 사람은 서버가 직무로 정한다 (팀원 → 스태프 → 팀장)
        receiverRole: t.defaultReceiverRole ?? null,
        baselineMinutes: t.baselineMinutes || null,
        dueOffsetHours: t.dueOffsetHours,
        dependsOn: t.dependsOn ?? [],
        taskList: t.taskList,
        // 운영진이 직접 고른 받는 사람만 보낸다 (아니면 서버가 직무 규칙으로 정한다)
        receiverId: t.receiverPinned && t.receiverId !== NPC_LEAD.id ? t.receiverId : null,
        skillTags: t.skillTags ?? [],
      })),
    ]),
  );

/**
 * Ops 콘솔 상태
 *   projects  목록 (요약)
 *   currentId 지금 보는 프로젝트 (null = New project)
 *   detail    지금 프로젝트 Setup 전체
 *   dirty     단계별 저장 안 한 변경
 */
export const opsReducer = (state, action) => {
  switch (action.type) {
    case 'list':
      return { ...state, projects: action.projects };
    case 'select':
      return { ...state, currentId: action.id, detail: null, dirty: {} };
    case 'set': {
      const p = action.project;
      const summary = {
        id: p.id,
        name: p.name,
        status: p.status,
        leaderStartAt: p.leaderStartAt,
        endAt: p.endAt,
      };
      const exists = state.projects.some((x) => x.id === p.id);
      return {
        ...state,
        currentId: p.id,
        detail: p,
        projects: exists
          ? state.projects.map((x) => (x.id === p.id ? summary : x))
          : [summary, ...state.projects],
        dirty: action.step ? { ...state.dirty, [action.step]: false } : state.dirty,
      };
    }
    case 'dirty':
      return state.dirty[action.step] === action.value
        ? state
        : { ...state, dirty: { ...state.dirty, [action.step]: action.value } };
    default:
      return state;
  }
};
